/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect } from 'react';
import { t } from 'i18next';
import { useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import moment from 'moment/moment';

// Component imports
import { Button, Card, Col, Divider, Empty, Grid, Menu, Modal, Radio, Row, Skeleton, Space, Spin, Tooltip, Typography, Form } from 'antd';
import DeleteConfirmationModal from '../Modals/DeleteConfirmationModal';
import LiveChatBotSettingsForm from './LiveChatBotSettingsForm';
import PluginSummarizationForm from './Plugins/PluginSummarizationForm';
import SideModal from '../Layout/SideModal';
import { ApiOutlined } from '@ant-design/icons';
import CustomSizeBadge from '../UI/CustomSizeBadge';
import PluginSemanticSearchNodeForm from './Plugins/PluginSemanticSearchNodeForm';

// Misc imports
import { DEFAULT_DATETIME_FORMAT } from '../../constants/main';
import { useLazyFetchIntegrationsQuery } from '../../store/integrations/integrationsApi';
import { NAV_ORGANIZATION_INTEGRATIONS } from '../../constants/navigation';
import { useFetchBotQuery, useUpdateBotMutation } from '../../store/bots/botsApi';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useModalConfirm } from '../../utils/hooks/useModalConfirm';
import { INTEGRATION_CATEGORY, INTEGRATION_TYPE, PLUGINS, INTEGRATIONS, PLUGIN_CATEGORY, SIDEBAR_GROUPS } from '../../constants/integrations';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import useTheme from '../../utils/hooks/useTheme';
import pickPrefix from '../../utils/string/pickPrefix';
import { isEmptyObject } from '../../utils/is/isEmptyObject';
import PluginLanguageDetectionForm from './Plugins/PluginLanguageDetectionForm';

// Constant declarations
const { useBreakpoint } = Grid;
const { Text, Paragraph } = Typography;

const BotPluginsForm = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const { orgId, tenantId } = useParams();
  const { botFormPanelHeight } = useTheme();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;

  const { data: organization } = getCurrentQueryState('fetchOrganization', { orgId });
  const [fetchIntegrations, { data: integrations, isFetching: isFetchingIntegrations }] = useLazyFetchIntegrationsQuery();
  const { data: bot, isLoading: isLoadingBot } = useFetchBotQuery({ orgId, tenantId });
  const [updateBot, { isLoading: isUpdatingBot }] = useUpdateBotMutation({ fixedCacheKey: `updateTenantSettings` });

  const [selectedIntegrationCategory, setSelectedIntegrationCategory] = useState();
  const [isActivateModalOpen, setIsActivateModalOpen] = useState();
  const [selectedIntegration, setSelectedIntegration] = useState();
  const [selectedPluginIntegration, setSelectedPluginIntegration] = useState();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState();
  const [isPluginSettingsModalOpen, setIsPluginSettingsModalOpen] = useState({});
  const [preActivationMode, setPreActivationMode] = useState(false);
  const [pendingPluginActivation, setPendingPluginActivation] = useState(null);
  const [canSubmitForm, setCanSubmitForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showModalConfirm } = useModalConfirm(canSubmitForm);

  const updateUrlSearchParams = async (category = INTEGRATION_CATEGORY.LIVECHAT) => {
    const urlSearchParams = new URLSearchParams(location.search);

    if (category) {
      urlSearchParams.set('scope', category);
    } else {
      urlSearchParams.delete('scope');
    }

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  const promptIfUnsavedChanges = (action) => {
    return showModalConfirm({
      defaultAction: () => {
        action();
      },
      onConfirm: async () => {
        await form.submit();
      },
      okText: t('Save Changes'),
      cancelText: t('Quit without saving'),
      title: t('Save Changes?'),
      content: t('Your unsaved changes will be lost. Save changes before closing?')
    });
  };

  const openPluginSettingsModal = (toolKey) => {
    setIsPluginSettingsModalOpen((prev) => ({ ...prev, [toolKey]: true }));
  };

  const closePluginSettingsModal = (toolKey) => {
    setCanSubmitForm(false);
    setIsPluginSettingsModalOpen((prev) => ({ ...prev, [toolKey]: false }));
  };

  useEffect(() => {
    const currentScope = searchParams.get('scope');
    const activePlugin = PLUGINS.find(p => p.category === currentScope);
    if (activePlugin) {
      setSelectedIntegrationCategory(activePlugin.supportedIntegrationCategory);
    } else {
      setSelectedIntegrationCategory(INTEGRATION_CATEGORY.LLM);
    }
  }, [bot, searchParams]);

  const closeModal = () => {
    setIsActivateModalOpen(false);
  };

  const onConnect = async () => {

    if(
      selectedPluginIntegration.pluginCategory === PLUGIN_CATEGORY.LLM_SUMMARIZATION
      || selectedPluginIntegration.pluginCategory === PLUGIN_CATEGORY.LLM_SEMANTIC_SEARCH_NODE
      || selectedPluginIntegration.pluginCategory === PLUGIN_CATEGORY.LLM_LANGUAGE_DETECTION
    ) {
      setPendingPluginActivation({
        category: selectedPluginIntegration.pluginCategory,
        integrationType: selectedPluginIntegration.type,
        integrationId: selectedIntegration.id
      });
      setPreActivationMode(true);
      closeModal();
      const toolKey = `${selectedPluginIntegration.type}_${selectedPluginIntegration.pluginCategory}`;
      openPluginSettingsModal(toolKey);
      return;
    }
    try {
      await addPlugin(selectedPluginIntegration.pluginCategory, selectedIntegration?.id);
    } catch (error) {
      return { error };
    }
    if (selectedIntegration?.type === INTEGRATION_TYPE.CISCO_LIVECHAT) {
      const toolKey = `${selectedIntegration?.type}_${PLUGIN_CATEGORY.LIVECHAT}`;
      openPluginSettingsModal(toolKey); // Open the settings modal directly
    }
  };

  const onDisconnect = async () => {
    removePlugin(selectedPluginIntegration?.pluginCategory, selectedPluginIntegration?.type, false);
    setIsDeleteModalOpen(false);
  };

  /**
   * Allows adding a new plugin to the bot. An integration can apply to multiple plugin categories
   * ex. An integration of type "LLM" can be applied to the plugin category LLM_NODE and LLM_TOPIC_MODELLING,
   * therefore specifying the plugin category as well as the integration id is mandatory here for connection
   * @param {string} pluginCategory - The category of the plugin that this integration will work as
   * @param {string} id - The id of the integration
   */
  const addPlugin = async (pluginCategory, id, settings = {}) => {
    let newPlugins = [];

    // Project the ids and append the one incoming from modal
    newPlugins = bot.plugins.map(plugin => {
      return {
        integrationId: plugin.integration.id,
        settings: plugin.settings,
        category: plugin.category
      };
    });

    newPlugins.push({
      integrationId: id,
      category: pluginCategory,
      ...(!isEmptyObject(settings) && { settings: settings })
    });

    try {
      const isSuccessful = await updateBot({ orgId, tenantId, data: { plugins: newPlugins } }).unwrap();

      if(isSuccessful) {
        closeModal();
      }
    } catch(error) {
      return { error };
    }
  };

  /**
   * Allows removing a specific plugin by plugin category and integration type. Integration id is not needed
   * here because plugin category and integration type are enough to describe all the uses
   * @param {*} pluginCategory - The category of the plugin that this integration will work as
   * @param {*} integrationType - The integration that the plugin has encapsulated
   */
  const removePlugin = async (pluginCategory, integrationType) => {
    let newPlugins = [];

    // Allow all integrations except the plugin category & integration type being disconnected
    newPlugins = bot.plugins
      .filter(plugin => !(plugin.category === pluginCategory && plugin.integration.type === integrationType))
      .map(plugin => {
        return {
          integrationId: plugin.integration.id,
          settings: plugin.settings,
          category: plugin.category
        };
      });

    // Send all the ids, if successful, close modal
    if (await updateBot({ orgId, tenantId, data: { plugins: newPlugins } }).unwrap()) {
      closeModal();
    }
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const getPluginOfIntegration = (integration) => {
    return bot?.plugins?.find(plugin => plugin.category === integration.pluginCategory && plugin.integration.type === integration.type);
  };

  const onPreactivatedPluginFormSuccess = (values, integration) => {
    const toolKey = `${integration.type}_${integration.pluginCategory}`;

    if(preActivationMode && pendingPluginActivation && pendingPluginActivation?.integrationType === integration.type) {
      addPlugin(
        pendingPluginActivation.category,
        pendingPluginActivation.integrationId,
        values
      ).then(() => {
        setPreActivationMode(false);
        setPendingPluginActivation(null);
        closePluginSettingsModal(toolKey);
      });
    } else {
      closePluginSettingsModal(toolKey);
    }
  };

  const getPluginCount = (pluginCategory) => {
    return INTEGRATIONS
      .filter(i => i.pluginCategory === pluginCategory)
      .filter(i => !i.isPluginHidden)
      .length;
  };

  const renderIntegrationSettings = (integration) => {
    const toolKey = `${integration.type}_${integration.pluginCategory}`;
    const isModalVisible = isPluginSettingsModalOpen[toolKey];
    const isPluginEnabled = getPluginOfIntegration(integration) || (preActivationMode && pendingPluginActivation?.integrationType === integration.type);
    return (
      <Fragment>
        <Button
          className="w-100"
          type="primary"
          onClick={() => {
            setSelectedPluginIntegration(integration);
            openPluginSettingsModal(toolKey);
          }}
          disabled={!isPluginEnabled}
          size="large"
        >
          {t('Settings')}
        </Button>

        <SideModal
          width={920}
          open={isModalVisible}
          title={`${t(integration.type, { context: 'type' })} - ${selectedPluginIntegration?.pluginCategory ? `${t(selectedPluginIntegration?.pluginCategory, { context: 'plugin title' })} Settings` : 'Settings'}`}
          onClose={() => promptIfUnsavedChanges(() => {
            closePluginSettingsModal(toolKey);
            setPreActivationMode(false);
            setPendingPluginActivation(null);
          })}
          destroyOnClose
          footer={
            <Row justify="space-between">
              <Col>
                {
                  (bot?.created && bot?.lastChanged) ? (
                    <Col>
                      <Typography.Paragraph type="secondary" className="mb-0 fs-sm" strong>
                        {t('Created')}: {moment.tz(bot.created, organization?.timezone).format(DEFAULT_DATETIME_FORMAT)}
                      </Typography.Paragraph>

                      <Typography.Paragraph type="secondary" className="mb-0 fs-sm" strong>
                        {t('Last modified')}: {bot.lastChangedBy?.name || ''}, {moment.tz(bot.lastChanged, organization?.timezone).format(DEFAULT_DATETIME_FORMAT)}
                      </Typography.Paragraph>
                    </Col>
                  ) : null
                }
              </Col>

              <Row justify="space-between">
                <Button
                  type="default"
                  className="mr-4"
                  size="large"
                  onClick={() => promptIfUnsavedChanges(() => {
                    closePluginSettingsModal(toolKey);
                    setPreActivationMode(false);
                    setPendingPluginActivation(null);
                  })}
                >
                  {t('Cancel')}
                </Button>

                <Button
                  disabled={!canSubmitForm || isLoading}
                  type="primary"
                  size="large"
                  loading={isLoading}
                  onClick={() => form.submit()}
                >
                  {t('Save Changes')}
                </Button>
              </Row>
            </Row>
          }
        >
          {
            (
              () => {
                switch(integration.type) {
                  case INTEGRATION_TYPE.OPEN_AI_LLM_SEMANTIC_SEARCH_NODE:
                  case INTEGRATION_TYPE.AZURE_AI_LLM_SEMANTIC_SEARCH_NODE:
                  case INTEGRATION_TYPE.AZURE_AI_LLM_SUMMARIZATION:
                  case INTEGRATION_TYPE.OPEN_AI_LLM_SUMMARIZATION:
                  case INTEGRATION_TYPE.GEMINI_AI_LLM_SEMANTIC_SEARCH_NODE:
                  case INTEGRATION_TYPE.GEMINI_AI_LLM_SUMMARIZATION:
                  case INTEGRATION_TYPE.OPEN_AI_LLM_LANGUAGE_DETECTION:
                  case INTEGRATION_TYPE.AZURE_AI_LLM_LANGUAGE_DETECTION:
                    switch(selectedPluginIntegration?.pluginCategory ) {
                      case PLUGIN_CATEGORY.LLM_SUMMARIZATION:
                        return (
                          <PluginSummarizationForm
                            form={form}
                            onCanSubmitChange={(value) => {
                              setCanSubmitForm(value);
                            }}
                            onLoadingChange={(value) => setIsLoading(value)}
                            onSuccess={(formValues) => {
                              onPreactivatedPluginFormSuccess(formValues, integration);
                            }}
                            isPreActivation={preActivationMode && pendingPluginActivation?.integrationType === integration.type}
                            pendingIntegrationType={pendingPluginActivation?.integrationType}
                          />
                        );
                      case PLUGIN_CATEGORY.LLM_SEMANTIC_SEARCH_NODE:
                        return <PluginSemanticSearchNodeForm
                          form={form}
                          onCanSubmitChange={(value) => {
                            setCanSubmitForm(value);
                          }}
                          onLoadingChange={(value) => setIsLoading(value)}
                          onSuccess={(formValues) => {
                            onPreactivatedPluginFormSuccess(formValues, integration);
                          }}
                          isPreActivation={preActivationMode && pendingPluginActivation?.integrationType === integration.type}
                          pendingIntegrationType={pendingPluginActivation?.integrationType}
                        />;
                      case PLUGIN_CATEGORY.LLM_LANGUAGE_DETECTION:
                        return <PluginLanguageDetectionForm
                          form={form}
                          onCanSubmitChange={(value) => {
                            setCanSubmitForm(value);
                          }}
                          onLoadingChange={(value) => setIsLoading(value)}
                          onSuccess={(formValues) => {
                            onPreactivatedPluginFormSuccess(formValues, integration);
                          }}
                          isPreActivation={preActivationMode && pendingPluginActivation?.integrationType === integration.type}
                          pendingIntegrationType={pendingPluginActivation?.integrationType}
                        />;
                      default:
                        return null;
                    }

                  case INTEGRATION_TYPE.HELVIA_LIVECHAT:
                  case INTEGRATION_TYPE.CISCO_LIVECHAT:
                  case INTEGRATION_TYPE.GENESYS_LIVECHAT:
                  case INTEGRATION_TYPE.ZENDESK_LIVECHAT:
                    return (
                      <LiveChatBotSettingsForm
                        enableAgentMaskingSettings={[INTEGRATION_TYPE.CISCO_LIVECHAT, INTEGRATION_TYPE.ZENDESK_LIVECHAT, INTEGRATION_TYPE.GENESYS_LIVECHAT].includes(integration.type)}
                        form={form}
                        onCanSubmitChange={(value) => setCanSubmitForm(value)}
                        onLoadingChange={(value) => setIsLoading(value)}
                        onSuccess={() => closePluginSettingsModal(toolKey)}
                      />
                    );
                  default:
                    return null;
                }
              }
            )()
          }
        </SideModal>
      </Fragment>
    );
  };

  const renderBadge = (status) => {
    return <CustomSizeBadge status={status} width={9} height={9} />;
  };

  const renderPluginIntegrations = (plugin) => {
    const doesPluginAllowMultipleIntegrations = PLUGINS.find(currentPlugin => plugin?.category === currentPlugin?.category)?.allowMultipleIntegrations;
    const shouldNotAllowSecondPlugin = !doesPluginAllowMultipleIntegrations && bot?.plugins?.find(botPlugin => botPlugin.category === plugin.category);

    const availableIntegrations = INTEGRATIONS
      .filter(integration => integration.category === selectedIntegrationCategory && integration.pluginCategory === plugin.category)
      .filter(integration => !integration.isPluginHidden)
      .filter(integration => {
        if (integration.pluginCategory !== PLUGIN_CATEGORY.LLM_LANGUAGE_DETECTION) {
          return true;
        }
        return bot?.systemSettings.isAgent;
      });
    return (
      <Row gutter={24} className="my-4">
        <Col flex="auto">
          <Space direction="vertical">
            <Row gutter={[16, 16]}>
              {
                availableIntegrations.map(integration => (
                  <Col key={integration.type}>
                    <Card
                      className={classNames('w-mc h-100 position-relative', {
                        'border-color-primary bg-primary-light': getPluginOfIntegration(integration)
                      })}
                      bodyStyle={{ height: '100%', minWidth: 265, padding: '16px' }}
                    >
                      <Row gutter={[8, 8]} className="h-100">
                        <Col span={24} className="align-content-center">
                          <Space>
                            {integration.renderTypeIcon({ className: 'fs-xxxl' })}
                            {t(integration.type, { context: 'type' })}
                          </Space>
                        </Col>

                        <Col span={24} style={{ minHeight: 45 }}>
                          <Text type="secondary" className="fs-sm">
                            {
                              getPluginOfIntegration(integration)
                                ? getPluginOfIntegration(integration)?.integration?.name
                                : t(integration.type, { context: 'type plugin description' })
                            }
                          </Text>
                        </Col>

                        <Col span={24} className="align-self-end w-100">
                          {
                            getPluginOfIntegration(integration) || (preActivationMode && pendingPluginActivation?.integrationType === integration.type) ? (
                              <Row gutter={8} wrap={false} align="middle" className="w-100">
                                <Col flex="12">
                                  <Button
                                    size="large"
                                    className="w-100"
                                    type="default"
                                    onClick={() => {
                                      setIsDeleteModalOpen(true);
                                      setSelectedPluginIntegration(integration);
                                    }}
                                  >
                                    {t('Deactivate')}
                                  </Button>
                                </Col>
                                {
                                  integration?.hasPluginSettings || (preActivationMode && pendingPluginActivation?.integrationType === integration.type) ? (
                                    <Fragment>
                                      <Col flex="12">
                                        {renderIntegrationSettings(integration)}
                                      </Col>
                                    </Fragment>
                                  ) : null
                                }
                              </Row>

                            ) : (
                              <Tooltip className="w-100" title={shouldNotAllowSecondPlugin ? t('Please deactivate the current plugin before activating a new one.') : ''}>
                                <Button
                                  className="w-100 d-inline-grid align-items-center"
                                  size="large"
                                  disabled={shouldNotAllowSecondPlugin}
                                  type="primary"
                                  onClick={async () => {
                                    setSelectedPluginIntegration(integration);

                                    // Directly activate Helvia Livechat or similar tools
                                    if (integration.type === INTEGRATION_TYPE.HELVIA_LIVECHAT) {
                                      await addPlugin(integration.pluginCategory, 'HELVIA_LIVECHAT');
                                      openPluginSettingsModal(integration.pluginCategory); // Open the settings modal directly
                                      return;
                                    } else {
                                      setIsActivateModalOpen(true);
                                      await fetchIntegrations({ orgId: orgId, urlParams: { type: integration.type } }).unwrap();
                                    }
                                  }}
                                >
                                  {t('Activate')}
                                </Button>
                              </Tooltip>
                            )
                          }
                        </Col>
                      </Row>

                      <div className="position-absolute right-3 top-0 mt-1">
                        {
                          getPluginOfIntegration(integration) ? (
                            renderBadge('success')
                          ) : (
                            renderBadge('default')
                          )
                        }
                      </div>
                    </Card>
                  </Col>
                ))
              }
            </Row>
          </Space>
        </Col>
      </Row>
    );
  };

  return (
    <Fragment>
      <Card className="mx-0 p-0">
        {
          isLoadingBot ? (
            <Row justify="center">
              <Col span={24} className="py-4">
                <Spin className="d-block" />
              </Col>
            </Row>
          ) : (

            <Row wrap={false} className="h-100">
              <Col flex="none" className="py-4 border-right">
                <Space direction="vertical">
                  <Typography.Text className="fw-600 ml-3">
                    <ApiOutlined className="fs-lg mr-1"/> {t('Plugin Categories')?.toLocaleUpperCase()}
                  </Typography.Text>

                  <Menu
                    theme="light"
                    className="border-0"
                    defaultSelectedKeys={searchParams.get('scope') || PLUGIN_CATEGORY.LLM_NODE}
                    onSelect={(e) => {
                      const selectedPlugin = PLUGINS.find(p => p.category === e.key);
                      if (selectedPlugin) {
                        setSelectedIntegrationCategory(selectedPlugin.supportedIntegrationCategory);
                      }
                      updateUrlSearchParams(e.key);
                    }}
                  >
                    {Object.values(SIDEBAR_GROUPS).map((groupTitle) => (
                      <Menu.ItemGroup key={groupTitle} title={t(groupTitle)}>
                        {PLUGINS
                          .filter((plugin) => plugin.sidebarGroup === groupTitle && !plugin.hidden)
                          .filter(plugin => {
                            if (plugin.category !== PLUGIN_CATEGORY.LLM_LANGUAGE_DETECTION) {
                              return true;
                            }
                            return bot?.systemSettings?.isAgent;
                          })
                          .map((plugin) => (
                            <Menu.Item key={plugin.category} className="fs-sm fw-600 my-0">
                              <Row justify="space-between">
                                <Text>{t(plugin.category, { context: 'plugin title' })}</Text>
                                <Text type="secondary" className="ml-2">{getPluginCount(plugin.category)}</Text>
                              </Row>
                            </Menu.Item>
                          ))}
                      </Menu.ItemGroup>
                    ))}
                  </Menu>
                </Space>
              </Col>
              <Col
                style={{ height: isMobileOrTabletSize ? 'auto' : botFormPanelHeight }}
                flex="auto"
                className="px-6 py-4 scrollbar-custom scrollbar-custom-nohide overflow-y-auto overflow-x-hidden"
              >
                {
                  PLUGINS
                    .filter(plugin => plugin.category === (searchParams.get('scope') || PLUGIN_CATEGORY.LLM_NODE))
                    .filter(plugin => {
                      if (plugin.category !== PLUGIN_CATEGORY.LLM_LANGUAGE_DETECTION) {
                        return true;
                      }
                      return bot?.systemSettings?.isAgent;
                    })
                    .map(plugin => (
                      <Row gutter={[16, 16]} className="mb-4" justify={'space-between'}>
                        <Col span={24}>
                          <Space.Compact direction="vertical" className="w-100 d-flex align-left" >
                            <Divider className="mt-2 mb-1 plugin-category-divider" orientation="left">
                              <Text className="fs-xl" strong>
                                {`${t(plugin.category, { context: 'plugin title' })}`}
                              </Text>
                            </Divider>
                            <Text type="secondary" className="fs-sm">
                              {t(plugin.category, { context: 'plugin description' })}
                            </Text>
                          </Space.Compact>
                        </Col>
                        <Col span={24}>
                          {renderPluginIntegrations(plugin)}
                        </Col>
                      </Row>
                    ))
                }
              </Col>
            </Row>
          )
        }
      </Card>

      <Modal
        title={
          <Fragment>
            <Space size={4}>
              {selectedPluginIntegration?.renderTypeIcon?.({ className: 'fs-xxxl' })}
              <Text className="ml-2">{t('Select')}</Text>
              <Text>{t(selectedPluginIntegration?.type, { context: 'type' })}</Text>
              <Text>{t('integration')}</Text>
            </Space>
            <Paragraph type="secondary" className="mb-0 mt-2">
              {pickPrefix(`${t(selectedPluginIntegration?.type, { context: 'type' })}`)} {t(selectedPluginIntegration?.type, { context: 'type' })} {t('integration is required to enable this plugin.')}
            </Paragraph>
          </Fragment>
        }
        open={isActivateModalOpen}
        onCancel={closeModal}
        centered
        footer={
          <Row justify="end">
            <Col>
              <Button
                type="link"
                size="large"
                onClick={() => window.open(`${replaceUrlParams(NAV_ORGANIZATION_INTEGRATIONS, { orgId })}`)}
              >
                {t('Add New Integration')}
              </Button>
            </Col>

            <Col>
              <Button
                type="primary"
                size="large"
                onClick={() => onConnect()}
                disabled={!selectedIntegration}
              >
                {t('Activate')}
              </Button>
            </Col>
          </Row>
        }
      >
        <Fragment>
          {
            isFetchingIntegrations ? (
              <Skeleton active />
            ) : integrations?.items?.length > 0 ? (
              <Radio.Group
                onChange={(e) => {
                  setSelectedIntegration(e.target.value);
                }}
                className="overflow-y-auto overflow-x-hidden scrollbar-custom w-100"
                style={{ maxHeight: 250 }}
              >
                <Space direction="vertical">
                  {
                    integrations?.items?.map((scope) => (
                      <Radio value={scope}>{scope.name}</Radio>
                    ))
                  }
                </Space>
              </Radio.Group>
            ) : (
              <Empty
                description={<Text type="secondary">{t('There are no integrations available for this plugin')}.</Text>}
              />
            )
          }
        </Fragment>
      </Modal>

      <DeleteConfirmationModal
        title={t('Deactivate this integration forever?')}
        content={t('This action will deactivate this integration and affect this agent.')}
        onDelete={onDisconnect}
        isDeleting={isUpdatingBot}
        isModalOpen={isDeleteModalOpen}
        closeModal={closeDeleteModal}
        deleteKeyword="Deactivate"
      />
    </Fragment>
  );
};

export default BotPluginsForm;
